import { ApplicationStatus } from 'modules/applications/domain/enums/application-status';

import { ApplicationDto } from 'modules/applications/domain/interfaces/application-dto';
import { mapTeamResponseDto } from 'modules/teams/domain/data-mappers/response-dto';
import { UpsertTournamentTeamDto } from 'modules/teams/domain/dtos/upsert-tournament-team.dto';
import { AnnouncedTeamsMap } from 'modules/teams/domain/interfaces/announced-teams-map';
import { GetTournamentTeam, GetTournamentTeams } from 'modules/teams/domain/interfaces/request-dto';
import { TeamResponseDto } from 'modules/teams/domain/interfaces/response-dto';
import { Team } from 'modules/teams/domain/interfaces/team';
import { ResponseGraphql } from 'shared/types/response-graphql';
import { parseResponseError } from 'shared/utils';
import graphqlResource, { GraphqlResource } from '../../../core/graphql-resource';
import { mapAnnouncedChampionshipTeams } from '../data-mappers/announced-teams.mapper';
import { TeamDto } from '../dtos/team.dto';
import { UpsertChampionshipApplicationResponseDto } from '../dtos/upsert-championship-application-response.dto';
import { UpsertChampionshipApplicationUserResponseDto } from '../dtos/upsert-championship-application-user-response.dto';
import { UpsertChampionshipApplicationUserDto } from '../dtos/upsert-championship-application-user.dto';
import { UpsertChampionshipApplicationDto } from '../dtos/upsert-championship-application.dto';
import { PlayerApplication } from '../interfaces/player-application';

export class TeamsRepository {
  constructor(private readonly graphql: GraphqlResource) {}

  public async getAnnouncedChampionshipTeams(championshipId: number) {
    const championshipApplications = await this.getChampionshipApplications(championshipId);

    return mapAnnouncedChampionshipTeams(championshipId, championshipApplications.filter(Boolean)) as AnnouncedTeamsMap;
  }

  public async upsertChampionshipApplicationForUser(params: UpsertChampionshipApplicationUserDto): Promise<PlayerApplication> {
    const query = `#graphql
      mutation upsertChampionshipApplicationUser($input: ChampionshipApplicationOneRowInput!) {
        upsertChampionshipApplicationUser(input: $input) {
          id
          team { id }
          championship { id }
          user {
            id
            name
            surname
            middleName
            birthDate
            isReal
            photo {
              id
              externalUrl
            }
            height
            weight
          }
          state
          position
          number
          createdAt
        }
      }
    `;

    const { id, ...input } = params;
    const variables = { input };

    const response = await this.graphql.query<UpsertChampionshipApplicationUserResponseDto>(query, variables);
    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const { upsertChampionshipApplicationUser: upsertedApplication } = response.data.data;

    return {
      id: upsertedApplication.id,
      teamId: upsertedApplication.team.id,
      championshipId: upsertedApplication.championship.id,
      player: {
        id: upsertedApplication.user.id,
        firstName: upsertedApplication.user.name,
        lastName: upsertedApplication.user.surname,
        middleName: upsertedApplication.user.middleName,
        birthDate: upsertedApplication.user.birthDate,
        logoUrl: upsertedApplication.user.photo?.externalUrl,
      },
      status: upsertedApplication.state,
      position: upsertedApplication.position,
      number: upsertedApplication.number,
      createdAt: upsertedApplication.createdAt,
    };
  }

  public async upsertChampionshipApplication(params: UpsertChampionshipApplicationDto) {
    const query = `#graphql
    mutation upsertChampionshipApplication($input: ChampionshipApplicationInput!) {
      upsertChampionshipApplication(input: $input) {
        team {
          id
          name
          shortName
          championshipApplications {
            user {
              id
            }
            state
          }
        }
        championship {
          id
          name
        }
        users {
          id
          name
          surname
          middleName
          birthDate
          photo {
            id
            externalUrl
          }
        }
      }
    }
    `;

    const variables = {
      input: {
        teamId: params.teamId,
        championshipId: params.championshipId,
        createUserOrUpdateApplications: params.applications.map((application) => ({
          userId: application.userId,
          name: application.name,
          surname: application.surname,
          middleName: application.middleName,
          phone: application.phone,
          birthDate: application.birthDate,
          height: application.height,
          weight: application.weight,
          number: application.number,
          position: application.position?.toUpperCase(),
          state: application.state,
        })),
      },
    };
    const response = await this.graphql.query<UpsertChampionshipApplicationResponseDto>(query, variables);

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const { upsertChampionshipApplication: upsertedApplication } = response.data.data;

    const users = upsertedApplication.users.map((user) => ({
      ...user,
      state: upsertedApplication.team.championshipApplications?.find((application) => application.user?.id === user.id)?.state,
    }));

    const state = users.some((user) => user.state === ApplicationStatus.ACCEPTED)
      ? ApplicationStatus.ACCEPTED
      : users.every((user) => user.state === ApplicationStatus.DECLINED)
        ? ApplicationStatus.DECLINED
        : ApplicationStatus.PENDING;

    return {
      championshipId: upsertedApplication.championship.id,
      team: { ...upsertedApplication.team, state },
      users,
    };
  }

  // TODO: check and maybe remove
  private async getChampionshipTeams(championshipId: number): Promise<TeamDto[]> {
    const query = `#graphql
      query getChampionshipTeams($championshipId: Int!) {
        championship(id: $championshipId) {
          teams {
            id
            name
            shortName
            createdAt
            emblem {
              id
              externalUrl
            }
            members {
              id
              position
              number
              user {
                id
                name
                surname
                middleName
                birthDate
                isReal
                photo {
                  id
                  externalUrl
                }
              }
            }
          }
        }
      }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{ data: { championship: { teams: TeamDto[] } } }>(query, variables);
    const { teams } = response.data.data.championship;

    return teams;
  }

  private async getChampionshipApplications(championshipId: number): Promise<ApplicationDto[]> {
    const query = `#graphql
      query getChampionshipAnnouncedTeams($championshipId: Int!) {
        championship(id: $championshipId) {
          championshipApplications {
            id
            team {
              id
              name
              shortName
              emblem {
                id
                externalUrl
              }
            }
            position
            number
            user {
              id
              name
              surname
              middleName
              birthDate
              photo {
                id
                externalUrl
              }
            }
            state
            createdAt
            updatedAt
          }
        }
      }
    `;

    const variables = { championshipId };
    const response = await this.graphql.query<{ data: { championship: { championshipApplications: ApplicationDto[] } } }>(
      query,
      variables,
    );
    const { championshipApplications } = response.data.data.championship;

    return championshipApplications;
  }

  public async getTournamentTeams(props: GetTournamentTeams): Promise<Team[]> {
    const query = `#graphql
    query getTournamentTeams($tournamentId: Int!, $sportType: SportType!, $limit: Int, $offset: Int, $namePart: String) {
      teamsPaged(input: {tournamentId: $tournamentId, sportType: $sportType, limit: $limit, offset: $offset, namePart: $namePart}) {
        id
        name
        shortName
        emblem {
          id
          externalUrl
        }
        createdAt
        members {
          id
          user {
            id
          }
        }
      }
    }
    `;

    const { tournamentId, sportType, limit, offset, namePart } = props;
    const response = await this.graphql.query<{ data: { teamsPaged: TeamResponseDto[] }; errors: any }>(query, {
      tournamentId,
      sportType: sportType.toUpperCase(),
      limit,
      offset,
      namePart,
    });

    if (response.data?.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    return mapTeamResponseDto(response.data?.data?.teamsPaged ?? []);
  }

  public async getTournamentTeam(props: GetTournamentTeam): Promise<Team> {
    const query = `#graphql
    query getTournamentTeam($id: Int!) {
      team(id: $id) {
        id
        name
        shortName
        emblem {
          id
          externalUrl
        }
        createdAt
        members {
          id
          user {
            id
            name
            surname
            middleName
            birthDate
            photo {
              externalUrl
            }
            height
            weight
            isReal
          }
          number
          position
        }
      }
    }
    `;

    const { teamId } = props;
    const response = await this.graphql.query<ResponseGraphql<{ team: TeamResponseDto }>>(query, { id: teamId });

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const team = response.data.data.team;

    return mapTeamResponseDto([team])[0];
  }

  public async upsertTeam(values: UpsertTournamentTeamDto): Promise<Team> {
    const query = `#graphql
    mutation upsertTeam($input: UpsertTeamInput!) {
      upsertTeam(input: $input) {
        id
        name
        shortName
        emblem {
          id
          externalUrl
        }
        createdAt
        members {
          id
        }
      }
    }
    `;

    const variables = {
      input: {
        id: values.id,
        name: values.name,
        shortName: values.shortName,
        tournamentId: values.tournamentId,
      },
    };

    const response = await this.graphql.query<ResponseGraphql<{ upsertTeam: TeamResponseDto }>>(query, variables);

    if (response.data.errors) {
      throw new Error(parseResponseError(response.data.errors[0]));
    }

    const result = response.data.data.upsertTeam;
    return mapTeamResponseDto([result] ?? [])[0];
  }
}

const teamsRepository = new TeamsRepository(graphqlResource);

export default teamsRepository;
